import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/about';
import { useState } from 'react';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9quff7nahb7382ovg70';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setSubmitting(false);
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '发送失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于黄油网页游戏合集',
    description: aboutInfo.description,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于黄油网页游戏合集
            </h1>
            <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.tagline}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-14">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">品牌故事</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{aboutInfo.story}</p>
            </div>

            <div className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">核心价值</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {aboutInfo.values.map((value) => (
                  <div key={value.title} className="p-5 bg-background-50 rounded-lg border border-background-200/70">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">玩家口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutInfo.testimonials.map((testimonial) => (
                <div key={testimonial.name} className="bg-background-50 p-5 rounded-lg border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{testimonial.content}&rdquo;</p>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{testimonial.name}</div>
                    <div className="text-xs text-foreground-400">{testimonial.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900 mb-0.5">邮箱</div>
                    <div className="text-sm text-foreground-500">{aboutInfo.contact.email}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-wechat-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900 mb-0.5">微信</div>
                    <div className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900 mb-0.5">地址</div>
                    <div className="text-sm text-foreground-500">{aboutInfo.contact.address}</div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 p-6 rounded-lg border border-background-200/70">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">发送消息</h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-300 transition-colors text-foreground-800"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-300 transition-colors text-foreground-800"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="您的留言（最多500字）"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-300 transition-colors text-foreground-800 resize-none"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? '发送中...' : '发送消息'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">消息已发送，感谢您的联系！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError || '发送失败，请稍后重试'}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}